import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Box } from '@mui/material'
import classNames from 'classnames/bind'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { selectRestaurant } from '~/redux/restaurantSlice'
import { EXTERNAL_PARTNER, MERCHANT_TYPE, baseLanguages } from '~/constants'
import STORAGE_KEYS from '~/constants/localStorage'
import { hidePopupTopPage } from '~/redux/popupTopPageSlice'
import styles from './styles/PopUpTopPage.module.css'
import ButtonConfirm from './ButtonConfirm'
import { KEY_PAGE_POPUP } from '~/constants/popupTopPage'
import { getParentType } from '~/utils/logEventUser'
import { USER_ACTIONS } from '~/constants/logEventUser'
import { logEventUserApi } from '~/apis/logEventUser'

const cx = classNames.bind(styles)

// Lựa chọn language
const LanguageSelection = ({ setKeyPage }) => {
  const {
    supportLang = [],
    tableUid,
    number_of_people,
    externalPartner,
    merchant_type,
    food_court_id,
    restaurantId,
    tableId,
  } = useSelector(selectRestaurant)
  const isTypeHotel = merchant_type === MERCHANT_TYPE.HOTEL
  const isTypeRestaurant = merchant_type === MERCHANT_TYPE.RESTAURANT
  const isTypeSouvenir = merchant_type === MERCHANT_TYPE.SOUVENIR
  const isPartnerSmaregi = externalPartner === EXTERNAL_PARTNER.Smaregi
  const dispatch = useDispatch()
  const { i18n, t } = useTranslation()
  const i18nLanguage = i18n.language
  const [currentLanguage, setCurrentLanguage] = useState(i18nLanguage)
  const numberOfPeopleLocal = localStorage.getItem(STORAGE_KEYS.NUMBER_OF_PEOPLE) // đánh dấu là đã chọn ngôn ngữ rồi lần sau không mở popup nữa

  const supportedLang = supportLang.map((langCode) => {
    return baseLanguages.find((lang) => lang.code === langCode)
  })
  /**
   * Xử lý check trường hợp ngôn ngữ trước đó không match với danh sách ngôn ngữ support
   * => lấy ngôn ngữ đầu tiên mà api trả về
   *  */
  useEffect(() => {
    if (!supportLang.includes(i18nLanguage)) {
      if (supportLang.length > 0) i18n.changeLanguage(supportLang[0])
    }
  }, [i18nLanguage, supportLang, i18n])

  useEffect(() => {
    setCurrentLanguage(i18nLanguage)
  }, [i18nLanguage])

  // Hàm xử lý chuyển trang
  const handleSwitchPage = () => {
    // Nếu trong trường hợp là hotel => di chuyển đến trang nhập thông tin hotel
    if (isTypeHotel) {
      setKeyPage(KEY_PAGE_POPUP.HOTEL)
      return
    }

    // Trong trường hợp partner là smaregi -> Di chuyển đến trang nhập số lượng người
    if (isPartnerSmaregi) {
      setKeyPage(KEY_PAGE_POPUP.SMAREGI)
      return
    }

    // Trường hợp default -> vẫn ở trang nhập language
    setKeyPage(KEY_PAGE_POPUP.LANGUAGE)
  }

  const handleLogEventUser = async () => {
    const localeFromLocal = localStorage.getItem(STORAGE_KEYS.LOCALE)

    // Nếu trước đó đã chọn ngôn ngữ hoặc type = hotel và chưa nhập thông tin phòng -> không log
    if (localeFromLocal || (isTypeHotel && !tableId)) return

    await logEventUserApi({
      parent_type: getParentType(merchant_type, food_court_id),
      parent_id: Number(food_court_id) || restaurantId,
      child_type: null,
      child_id: null,
      locale: currentLanguage,
      position_id: tableId || null,
      action: USER_ACTIONS.scan_qrcode,
    })
  }

  /**
   * Nếu trong trường hợp chưa nhập form hotel -> di chuyển đến trang hotel
   * Trường hợp đã nhập -> đóng popup và lưu locale vào storage
   */
  const handleConfirmLanguage = () => {
    // Xử lý lưu log
    handleLogEventUser()

    localStorage.setItem(STORAGE_KEYS.LOCALE, currentLanguage) // đánh dấu là đã chọn ngôn ngữ rồi lần sau không mở popup nữa
    i18n.changeLanguage(currentLanguage) // chuyển ngôn ngữ i18n

    // Xử lý trong trường hợp là đồ lưu niệm, restaurant type -> Ẩn luôn popup language không cần chuyển trang
    if (isTypeSouvenir || isTypeRestaurant) {
      dispatch(hidePopupTopPage())
      return
    }

    // Xử lý trong trường hợp là hotel mà đã nhập đầy đủ thông tin phòng(table uuid) rồi thì đóng popup
    if (tableUid && isTypeHotel) {
      dispatch(hidePopupTopPage())
      return
    }

    // Xử lý trong trường hợp là smaregi. Nếu đã nhập số người rồi thì đóng popup luôn
    if (tableUid && (number_of_people || numberOfPeopleLocal) && isPartnerSmaregi) {
      dispatch(hidePopupTopPage())
      return
    }

    // Xử lý chuyển trang -> hotel hoặc people
    handleSwitchPage()
  }

  // Hàm xử lý thay đổi language state
  const changeCurrentLanguage = (languageCode) => {
    setCurrentLanguage(languageCode)
  }

  const renderLanguagesList = () => {
    return supportedLang.map((item) => {
      const Flag = item.image

      const active = currentLanguage === item.code

      return (
        <Box
          key={item.id}
          onClick={() => changeCurrentLanguage(item.code)}
          className={cx('item_language')}
          bgcolor="baseColor.color11"
          boxShadow={active ? '0px 4px 8px rgba(0, 0, 0, 0.25)' : undefined}
          sx={{
            opacity: active ? 1 : 0.32,
          }}>
          <Box display="flex" borderRadius="4px">
            <Flag />
          </Box>
          <Box className={cx('name_language')}>{item.name}</Box>
        </Box>
      )
    })
  }

  // Hiển thị nút next page. Trong trường hợp đã nhập form hotel rồi -> hiển thị nút done
  const renderActionLanguage = () => {
    return (
      <ButtonConfirm
        onClick={handleConfirmLanguage}
        label={tableUid ? t('actions.done') : t('actions.next')}
      />
    )
  }

  return (
    <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
      <Box className={cx('languages_list')}>{renderLanguagesList()}</Box>
      {renderActionLanguage()}
    </Box>
  )
}

LanguageSelection.propTypes = {
  setKeyPage: PropTypes.func,
}

export default React.memo(LanguageSelection)
